import React, { useState, useMemo, useEffect } from "react";
import {
    Box,
    Button,
    Divider,
    Dialog,
    DialogContent,
    DialogActions,
    DialogTitle,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
    Tooltip,
    Snackbar,
    Alert,
    InputAdornment,
    IconButton
} from "@mui/material/";
import { Cancel, Edit, Save, CheckCircle } from "@mui/icons-material";
import debounce from "lodash/debounce";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import SearchIcon from "@mui/icons-material/Search";
import makeStyles from "@mui/styles/makeStyles";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";
import actions from "../../../../Store/actions";
import {
    useAssetTypeIdentifiers,
    useAssetTypesForOrganisation,
    useAssetTypeMakes,
    useAssetTypeValidationRules
} from "../../../../common/hooks";
import { strings } from "../../../../common/i18n";
import { helpers } from "../../../../common/lib";
import CloseIcon from "@mui/icons-material/Close";
import DatasetLinkedIcon from "@mui/icons-material/DatasetLinked";
import styles from "./styles";
import { logToSentry } from "../../../../config/Sentry";
import ImageCarousel from "./ImageCarousel/";
import Loading from "../../../../common/components/Loading";

const useLocalStyles = makeStyles(styles);
const LOOKUP_DEBOUNCE_MS = 500;

export default () => {
    const { altAssets, typesList, assetLookup, userOrganisations, vinDecoder } =
        useSelector((state) => state);
    const [autoFilledMake, setAutoFilledMake] = useState(false);
    const [data, setData] = useState({});
    const [assetLookupData, setAssetLookupData] = useState({});
    const [dialogOpen, setDialogOpen] = useState(false);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const { usesVehicleLookup } = userOrganisations.item;

    const params = useParams();
    const assetTypes = useAssetTypesForOrganisation();
    const classes = useLocalStyles();
    const dispatch = useDispatch();

    const identifiers = useAssetTypeIdentifiers(data.type);
    const makes = useAssetTypeMakes(data.type);
    const rules = useAssetTypeValidationRules(data.type);

    // Shared validation for any identifier field
    const FIELD_ID_MAP = { identifier1: 1, identifier2: 2, identifier3: 3, identifier4: 4 };

    const getRule = (name) => {
        const fieldId = FIELD_ID_MAP[name];
        if (!fieldId || !data.type) return null;
        return (
            typesList?.validationRules?.data?.find(
                
                (rule) => rule.assetTypeId == data.type && rule.fieldId === fieldId &&
                    (rule.invalidChar || rule.maxLength || rule.minLength || rule.regex)
            ) || null
        );
    };

    
    const validateIdentifierValue = (name, text, isBlur = false) => {
        if (name === "identifier1" && isBlur && !text.length) {
            return strings("errors.fieldRequired");
        }

        const rule = getRule(name);
        if (!rule || !text) return null;

        const { maxLength, minLength, invalidChar } = rule;
        const regex = helpers.regex.parseString(rule.regex);

        // 1. Forbidden characters list (e.g. "Q,O,I" for VINs)
        if (invalidChar) {
            const forbidden = invalidChar
                .split(",")
                .map((c) => c.trim())
                .filter(Boolean);
            const found = forbidden.filter((c) =>
                text.toUpperCase().includes(c.toUpperCase())
            );
            if (found.length) {
                return strings("errors.invalidCharacters", {
                    invalidChar: found.join(", ")
                });
            }
        }

        // 2. Regex pattern
        if (regex && !regex.test(text)) {
            const bad = helpers.regex.getInvalidCharacters(regex, text).join(", ");
            return strings("errors.invalidCharacters", { invalidChar: bad || text });
        }

        // 3. Max length (real-time)
        if (maxLength && text.length > maxLength) {
            return strings("errors.maxLength", { maxLength });
        }

        // 4. Min length (only on blur so we don't nag while typing)
        if (isBlur && minLength && text.length < minLength) {
            return strings("errors.minLength", { minLength });
        }

        return null;
    };

    const handleChange = (name) => (event) => {
        const { value } = event.target;
        const currentVin = data?.identifier1;
        setData((prev) => ({
            ...prev,
            [name]: value,
             ...(name === "type" ? { make: "" } : {})
        }));

        if (name === "make") {
            setAutoFilledMake(false);
        }

        if (name === "type" && currentVin) {
            triggerVinDecoderLookup(currentVin);
        }

        // Real-time validation: invalid chars, regex, max length
        const error = validateIdentifierValue(name, value, false);
        setErrors((prev) => {
            const next = { ...prev };
            if (error) {
                next[name] = error;
            } else {
                delete next[name];
            }
            return next;
        });
    };

    const debouncedAssetLookup = useMemo(
        () =>
            debounce(async (identifier2) => {
                try {
                    await dispatch(
                        actions.assetLookup.create.postRequest({
                            identifier2
                        })
                    );
                } catch (error) {
                    console.error(error);
                    logToSentry("On fetching asset look up:", { error });
                    dispatch(
                        actions.toast.create.error(
                            strings("errors.onFetchingAssetLookup")
                        )
                    );
                }
            }, LOOKUP_DEBOUNCE_MS),
        [dispatch]
    );

    const debouncedVinDecoderLookup = useMemo(
        () =>
            debounce(async (vin) => {
                try {
                    await dispatch(
                        actions.vinDecoder.create.getRequest(vin)
                    );
                } catch (error) {
                    console.error(error);
                    logToSentry("On fetching vin decoder:", { error });
                    dispatch(
                        actions.toast.create.error(
                            strings("errors.onFetchingVinDecoder")
                        )
                    );
                }
            }, LOOKUP_DEBOUNCE_MS),
        [dispatch]
    );

    const isValidVin = (vin = "") => {
        const trimmed = vin.trim().toUpperCase();
        return !validateIdentifierValue("identifier1", trimmed, true);
    };
    const triggerVinDecoderLookup = (vin) => {
        if (!isValidVin(vin)) {
            logToSentry("Invalid VIN provided for VIN decoder request", {
                vin,
                altAssetId: data?.id,
                identifier1: data?.identifier1,
                type: data?.type
            });
            dispatch(
                actions.toast.create.error(strings("errors.invalidVinFormat"))
            );
            return;
        }

        debouncedVinDecoderLookup(vin.trim().toUpperCase());
    };

    useEffect(() => {
        if (!usesVehicleLookup) return;
        if (!data?.identifier2 || data?.identifier2?.length < 4) return;

        debouncedAssetLookup(data.identifier2);

        return () => {
            debouncedAssetLookup.cancel(); // cleanup on unmount/change
        };
    }, [usesVehicleLookup, data.identifier2, debouncedAssetLookup]);

   useEffect(() => {
        if (!data?.type) return;
        if (!vinDecoder?.make) return;
        if (!Array.isArray(makes) || !makes.length) return;

        const normalize = (value = "") =>
            value.toString().trim().toLowerCase().replace(/[\s-_]+/g, "");

        const decodedMake = normalize(vinDecoder.make);

        const matchedMake = makes.find(
            (item) => normalize(item.name) === decodedMake
        );

        if (matchedMake?.id) {
            setData((prev) => ({
                ...prev,
                make: matchedMake.id
            }));
            setAutoFilledMake(true);
        } else {
            setData((prev) => ({
                ...prev,
                make: ""
                }));
            setAutoFilledMake(false);
            }
    }, [data.type, makes, vinDecoder?.make]);

    /** asset look up for the identifier2 */
    useEffect(() => {
        if (assetLookup) {
            setAssetLookupData({
                vin: assetLookup.identifier1,
                reg: assetLookup.identifier2,
                make:
                    typesList.makes.data.find(
                        (type) => type.id === assetLookup.make
                    )?.name || "",
                internalRef: assetLookup.internalReference,
                makeVal: assetLookup.make,
                assetType:
                    typesList.assetTypes.data.find(
                        (type) => type.id === assetLookup.type
                    )?.name || "",
                assetTypeVal: assetLookup.type
            });
        }
    }, [assetLookup, typesList]);

    
    useEffect(() => {
        if (!dialogOpen) return;
        const initialErrors = {};
        for (const name of ["identifier1", "identifier2", "identifier3", "identifier4"]) {
            const val = data[name] || "";
            const error = validateIdentifierValue(name, val, true);
            if (error) initialErrors[name] = error;
        }
        setErrors(initialErrors);
    }, [dialogOpen]); 

    const hasAssetLookupData = useMemo(
        () =>
            Boolean(
                usesVehicleLookup &&
                    assetLookupData &&
                    Object.values(assetLookupData).some(Boolean)
            ),
        [usesVehicleLookup, assetLookupData]
    );

    const handleCopyFromRight = () => {
        setData((prev) => ({
            ...prev,
            identifier1: assetLookupData?.vin || "",
            identifier2: assetLookupData?.reg || "",
            make: assetLookupData?.makeVal || "",
            type: prev.type // untouched
        }));
    };

    const handleIdentifierBlur = (name) => (event) => {
        const text = event.target.value;
        const error = validateIdentifierValue(name, text, true);
        setErrors((prev) => {
            const next = { ...prev };
            if (error) {
                next[name] = error;
            } else {
                delete next[name];
            }
            return next;
        });
    };

    const makeIdentifier = (name) =>
        identifiers[name] ? (
            <Grid item xs={12}>
                <TextField
                    fullWidth
                    label={identifiers[name]}
                    value={data[name] || ""}
                    onChange={handleChange(name)}
                    onBlur={handleIdentifierBlur(name)}
                    error={Boolean(errors[name])}
                    helperText={errors[name] || " "}
                    variant="outlined"
                />
            </Grid>
        ) : null;

    const makeSelect = (name, label, items) => (
        <Grid item xs={12}>
            <FormControl fullWidth variant="outlined">
                <InputLabel>{strings(label)}</InputLabel>
                <Select
                    label={strings(label)}
                    value={data[name] || ""}
                    onChange={handleChange(name)}
                >
                    {items.map((item) => (
                        <MenuItem key={item.id} value={item.id}>
                            {item.name}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        </Grid>
    );

    const onClose = () => !loading && setDialogOpen(false);

    const onEdit = () => {
        try {
            if (params.altAssetId) {
                const next = helpers.getListItemForId(
                    altAssets,
                    params.altAssetId
                );
                setData(next);
                setErrors({});
                setDialogOpen(true);
            }
        } catch (error) {
            console.error(error);
            logToSentry("On edit alt asset:", { error });
            dispatch(actions.toast.create.error(strings("errors.onEditAsset")));
        }
    };

    const onSave = async () => {
        try {
            setLoading(true);
            const {
                id,
                identifier1,
                identifier2,
                identifier3,
                identifier4,
                make,
                type
            } = data;

            await dispatch(
                actions.altAssets.create.patchRequest({
                    id,
                    identifier1,
                    identifier2,
                    identifier3,
                    identifier4,
                    make,
                    type
                })
            );
            await dispatch(
                actions.altAssets.create.getByIdRequest(
                    parseInt(params.altAssetId),
                    parseInt(params.auditId)
                )
            );
            dispatch(
                actions.toast.create.success(strings("updatedSuccessfully"))
            );
            onClose();
        } catch (error) {
            console.error(error);
            logToSentry("On saving alt asset:", { error });
            dispatch(
                actions.toast.create.error(strings("errors.onSavingAsset"))
            );
        }
        setLoading(false);
    };

    const onValidate = () => {
        // Existing inline errors (set by change/blur handlers)
        if (Object.keys(errors).length) return false;
        // Always require identifier1
        if (!data.identifier1?.trim()) return false;
        // Run full blur-level validation across all identifier fields
        for (const name of ["identifier1", "identifier2", "identifier3", "identifier4"]) {
            const val = data[name] || "";
            if (validateIdentifierValue(name, val, true)) return false;
        }
        return true;
    };

    const onRotateImage = async (item, angle) => {
        try {
            const { path, ...filteredData } = item;
            const res = await dispatch(
                actions.auditImages.create.patchRequest({ ...filteredData, ...angle })
            );
            const altAsset = helpers.getListItemForId(altAssets, params.altAssetId);
            const idx = altAsset.altAssetImages.findIndex(
                (i) => i.image.id === res.auditImage.id
            );
            if (idx !== -1) {
                altAsset.altAssetImages[idx].image = {
                    ...altAsset.altAssetImages[idx].image,
                    ...res.auditImage
                };
                dispatch(actions.altAssets.create.merge([altAsset]));
                setData((prev) => {
                    const imgs = [...prev.altAssetImages];
                    imgs[idx] = { ...imgs[idx], image: { ...imgs[idx].image, ...res.auditImage } };
                    return { ...prev, altAssetImages: imgs };
                });
            }
        } catch (error) {
            logToSentry("On rotation:", { error });
            dispatch(actions.toast.create.error("Error rotating image"));
        }
    };

    const onCropImage = async (item) => {
        try {
            const res = await dispatch(
                actions.auditImages.create.postRequest({
                    imageId: item.id,
                    auditId: parseInt(params.auditId)
                })
            );
            const altAsset = helpers.getListItemForId(altAssets, params.altAssetId);
            const idx = altAsset.altAssetImages.findIndex(
                (i) => i.image.id === res.auditImage.id
            );
            if (idx !== -1) {
                altAsset.altAssetImages[idx].image = {
                    ...altAsset.altAssetImages[idx].image,
                    ...res.auditImage
                };
                dispatch(actions.altAssets.create.merge([altAsset]));
                setData((prev) => {
                    const imgs = [...prev.altAssetImages];
                    imgs[idx] = { ...imgs[idx], image: { ...imgs[idx].image, ...res.auditImage } };
                    return { ...prev, altAssetImages: imgs };
                });
            }
        } catch (error) {
            logToSentry("On crop:", { error });
            dispatch(actions.toast.create.error("Error cropping image"));
        }
    };

    const handleSwapIdentifiers = () => {
        setData((prev) => ({
            ...prev,
            identifier1: prev.identifier2 || "",
            identifier2: prev.identifier1 || ""
        }));
    };

    const makeIdentifierWithTooltip = (name, tooltip) => {
        const value = data[name] || "";

        return identifiers[name] ? (
            <Grid item xs={12} sx={{ mb: 0, pb: 0 }}>
                <Tooltip title={tooltip} arrow placement="top-start">
                    <TextField
                        fullWidth
                        size="small"
                        margin="dense"
                        label={identifiers[name]}
                        value={value}
                        onChange={handleChange(name)}
                        onBlur={handleIdentifierBlur(name)}
                        error={Boolean(errors[name])}
                        helperText={
                            <Box display="flex" alignItems="center" gap={1}>
                                {errors[name] || " "}
                            </Box>
                        }
                        variant="outlined"
                    />
                </Tooltip>
            </Grid>
        ) : null;
    };

    const makeSelectWithTooltip = (name, label, items, tooltip) => (
        <Grid item xs={12}>
            <Tooltip title={tooltip} arrow placement="top-start">
                <FormControl fullWidth variant="outlined">
                    <InputLabel>{strings(label)}</InputLabel>
                    <Select
                        size="small"
                        margin="dense"
                        label={strings(label)}
                        value={data[name] || ""}
                        onChange={handleChange(name)}
                    >
                        {items.map((item) => (
                            <MenuItem key={item.id} value={item.id}>
                                {item.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Tooltip>
        </Grid>
    );

    function highlightMatch(fullText = "", subText = "") {
        if (!subText) return fullText;

        const regex = new RegExp(`(${subText})`, "i"); // case-insensitive
        const parts = fullText.split(regex);

        return parts.map((part, index) =>
            regex.test(part) ? (
                <Box
                    component="span"
                    key={index}
                    sx={{
                        backgroundColor: "primary.main",
                        fontWeight: "bold",
                        color: "white",
                        px: 0.5,
                        borderRadius: 0.5
                    }}
                >
                    {part}
                </Box>
            ) : (
                part
            )
        );
    }

    if (loading) return <Loading loadingText="updatingAsset" />;

    return (
        <>
            <Button fullWidth onClick={onEdit} size="small">
                {strings("editDetails")}
                <div className={classes.flexGrow} />
                <Edit />
            </Button>
            <Divider className={classes.fullWidth} />
            <Dialog
                fullWidth
                maxWidth="lg"
                onClose={onClose}
                open={dialogOpen}
                PaperProps={{ sx: { height: "85vh", overflow: "hidden" } }}
            >
                <DialogTitle
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        py: 1.5,
                        px: 2.5,
                        flexShrink: 0
                    }}
                >
                    <Typography variant="subtitle1" fontWeight={600}>
                        {strings("editDetails")}
                    </Typography>
                    <IconButton
                        aria-label="close"
                        onClick={onClose}
                        disabled={loading}
                        size="small"
                        sx={{ color: (theme) => theme.palette.grey[500] }}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>

                <Divider />

                {/* Two-panel body: left scrolls, right image fills */}
                <DialogContent
                    sx={{
                        display: "flex",
                        p: 0,
                        flex: 1,
                        minHeight: 0,
                        overflow: "hidden"
                    }}
                >
                    {/* LEFT: scrollable form */}
                    <Box
                        sx={{
                            width: 400,
                            flexShrink: 0,
                            minHeight: 0,
                            display: "flex",
                            flexDirection: "column",
                            borderRight: "1px solid",
                            borderColor: "divider",
                            overflowY: "auto"
                        }}
                    >
                        <Box sx={{ px: 2.5, py: 2, flex: 1 }}>
                            <Grid container spacing={1.5}>
                                <Grid item xs={12}>
                                    {makeIdentifierWithTooltip(
                                        "identifier1",
                                        "Unique vehicle VIN number"
                                    )}
                                    <Box
                                        sx={{
                                            display: "flex",
                                            justifyContent: "center",
                                            my: 0.5
                                        }}
                                    >
                                        <Button
                                            onClick={handleSwapIdentifiers}
                                            sx={{
                                                minWidth: 0,
                                                width: 28,
                                                height: 28,
                                                borderRadius: "50%",
                                                border: "1px solid",
                                                borderColor: "divider"
                                            }}
                                        >
                                            <SwapVertIcon fontSize="small" />
                                        </Button>
                                    </Box>
                                    {makeIdentifierWithTooltip(
                                        "identifier2",
                                        "Vehicle registration number"
                                    )}
                                </Grid>

                                <Grid item xs={12}>
                                    {makeIdentifierWithTooltip(
                                        "identifier3",
                                        "Manufacturer name"
                                    )}
                                </Grid>

                                <Grid item xs={12}>
                                    {makeSelectWithTooltip(
                                        "make",
                                        "make",
                                        makes,
                                        "Select the make (manufacturer)"
                                    )}
                                    {autoFilledMake && (
                                        <Alert severity="info" sx={{ mt: 0.75, py: 0 }}>
                                            Make auto-filled from VIN decoder
                                        </Alert>
                                    )}
                                </Grid>

                                <Grid item xs={12}>
                                    {makeSelectWithTooltip(
                                        "type",
                                        "assetType",
                                        assetTypes,
                                        "Select the asset type or category"
                                    )}
                                </Grid>

                                {/* Asset Lookup Data */}
                                {hasAssetLookupData && (
                                    <Grid item xs={12}>
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 1,
                                                mb: 0.5
                                            }}
                                        >
                                            <Tooltip title={strings("copy")}>
                                                <IconButton
                                                    size="small"
                                                    onClick={handleCopyFromRight}
                                                    sx={{
                                                        border: "1px dashed",
                                                        borderColor: "divider"
                                                    }}
                                                >
                                                    <ArrowBackIcon
                                                        color="primary"
                                                        fontSize="small"
                                                    />
                                                </IconButton>
                                            </Tooltip>
                                            <DatasetLinkedIcon
                                                color="primary"
                                                fontSize="small"
                                            />
                                            <Typography
                                                variant="subtitle2"
                                                fontWeight={600}
                                            >
                                                {strings("assetLookup")}
                                            </Typography>
                                        </Box>
                                        <Divider sx={{ mb: 0.75 }} />
                                        {[
                                            ["vin", assetLookupData?.vin],
                                            ["reg", assetLookupData?.reg],
                                            ["make", assetLookupData?.make],
                                            ["internalReference", assetLookupData?.internalRef],
                                            ["assetType", assetLookupData?.assetType]
                                        ].map(([label, value]) => (
                                            <Box
                                                key={label}
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    px: 0.75,
                                                    py: 0.25,
                                                    borderRadius: 0.75,
                                                    "&:hover": { backgroundColor: "action.hover" }
                                                }}
                                            >
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    sx={{ minWidth: 120 }}
                                                >
                                                    {strings(label)}
                                                </Typography>
                                                <Typography variant="body2" fontWeight={500}>
                                                    {value || "—"}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Grid>
                                )}
                            </Grid>

                            {/* blank area for next section */}
                            <Box sx={{ mt: 2, minHeight: 80 }} />
                        </Box>

                    </Box>

                    {/* RIGHT: full-height image panel */}
                    <Box
                        sx={{
                            flex: 1,
                            minHeight: 0,
                            overflowY: "auto",
                            position: "relative",
                            display: "flex",
                            flexDirection: "column"
                        }}
                    >
                        <ImageCarousel
                            images={data.altAssetImages}
                            onRotate={onRotateImage}
                            onCrop={onCropImage}
                        />
                    </Box>
                </DialogContent>

                <Divider />
                <DialogActions sx={{ px: 2, py: 1 }}>
                    <Button
                        disabled={loading}
                        onClick={onClose}
                        variant="outlined"
                        color="error"
                        size="small"
                    >
                        {strings("cancel")}{" "}
                        <Cancel fontSize="small" sx={{ ml: 0.5 }} />
                    </Button>
                    <Button
                        disabled={loading || !onValidate()}
                        onClick={onSave}
                        variant="outlined"
                        color="primary"
                        size="small"
                    >
                        {strings("save")}{" "}
                        <Save fontSize="small" sx={{ ml: 0.5 }} />
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};
